import MapboxDraw, { DrawCustomMode } from "@mapbox/mapbox-gl-draw";

import CustomSnapLineMode from "./snap/CustomSnapLineMode";
import { removeGuides } from "./helper";

const { constants: Constants } = MapboxDraw;

/**
 * Checks if the given coordinates are valid (not null, undefined, or NaN).
 */
function isValidCoordinate(lng: number, lat: number): boolean {
  return lng != null && lat != null && !Number.isNaN(lng) && !Number.isNaN(lat);
}

/**
 * Checks if the current click is a double-click on the last vertex (line completion).
 * Uses pixel-based proximity matching like the original MapboxDraw.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isDoubleClickCompletion(state: any, e: MapboxDraw.MapMouseEvent): boolean {
  const { lib } = MapboxDraw;
  return (
    (state.currentVertexPosition > 0 && lib.isEventAtCoordinates(e, state.line.coordinates[state.currentVertexPosition - 1])) ||
    (state.direction === "backwards" && lib.isEventAtCoordinates(e, state.line.coordinates[state.currentVertexPosition + 1]))
  );
}

/**
 * Checks if the snapped coordinates match the last vertex (line completion via snapping).
 * When snapping is active, the raw event coordinates (e.lngLat) may differ from the stored
 * vertex coordinates (which are snapped), so isDoubleClickCompletion can fail. This function
 * mirrors the completion check in SnapLineMode.onClick using snapped coordinates instead.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isSnappedCompletion(state: any, lng: number, lat: number): boolean {
  if (state.currentVertexPosition > 0) {
    const lastVertex = state.line.coordinates[state.currentVertexPosition - 1];
    if (lastVertex[0] === lng && lastVertex[1] === lat) {
      return true;
    }
  }
  if (state.direction === "backwards") {
    const nextVertex = state.line.coordinates[state.currentVertexPosition + 1];
    if (nextVertex && nextVertex[0] === lng && nextVertex[1] === lat) {
      return true;
    }
  }
  return false;
}

const drawLineString: DrawCustomMode = {
  ...CustomSnapLineMode,

  onSetup: function (opts) {
    // We store the mode options so that we can access them later to reinitialize the mode if needed (e.g. after undo/redo or when drawing multiple lines with multiCreate)
    this._ctx.options.modeOptions = opts || {};

    const state = CustomSnapLineMode.onSetup?.call(this, opts);
    state.maxVertices = this._ctx.options.maxVertices;
    // Determine whether to allow multiple line strings to be drawn in a single session based on the multiCreate option
    state.multiCreate = opts.multiCreate ?? true;
    removeGuides.call(this);

    return state;
  },

  onClick: function (state, e) {
    const lng = state.snappedLng;
    const lat = state.snappedLat;

    // Guard against invalid coordinates (can happen when onClick is called after mode change)
    if (!isValidCoordinate(lng, lat)) {
      return;
    }

    // Check if this is a completion (double-clicking the same point) with multiCreate enabled.
    // We check both raw event coordinates and snapped coordinates because when snapping,
    // the raw event position can differ from the stored (snapped) vertex coordinates.
    if ((isDoubleClickCompletion(state, e) || isSnappedCompletion(state, lng, lat)) && state.multiCreate) {
      // Handle completion ourselves to avoid flickering to simple_select
      this.changeMode(Constants.modes.DRAW_LINE_STRING, this._ctx.options.modeOptions);
      return;
    }

    CustomSnapLineMode.onClick?.call(this, state, e);

    // The in-progress line carries a trailing vertex that follows the cursor.
    if (state.maxVertices && state.line.coordinates.length === state.maxVertices + 1) {
      if (state.multiCreate) {
        this.changeMode(Constants.modes.DRAW_LINE_STRING, this._ctx.options.modeOptions);
      } else {
        this.changeMode(Constants.modes.SIMPLE_SELECT, { featureIds: [state.line.id] });
      }
    }
  },

  onStop: function (state) {
    // Call parent to handle cleanup, ghost removal, and CREATE event
    CustomSnapLineMode.onStop?.call(this, state);

    // Clear stored options when exiting the mode (not continuing to draw)
    if (!state.multiCreate) {
      this._ctx.options.modeOptions = {};
    }
  },

  onMouseDown: function (state, e) {
    // Check for right-click (button 2) to complete or cancel the line
    if (e.originalEvent?.button === 2) {
      const { modeOptions } = this._ctx.options;

      if (state.multiCreate) {
        // Always stay in draw mode when multiCreate is enabled (both completion and cancellation)
        this.changeMode(Constants.modes.DRAW_LINE_STRING, modeOptions);
      } else if (state.currentVertexPosition >= 2) {
        // Complete the line and select it
        this.changeMode(Constants.modes.SIMPLE_SELECT, { featureIds: [state.line.id] });
      } else {
        // Cancel drawing - exit to simple_select
        this.changeMode(Constants.modes.SIMPLE_SELECT);
      }
      return;
    }

    // Call the parent mode's onMouseDown for normal left-click behavior
    CustomSnapLineMode.onMouseDown?.call(this, state, e);
  },
};

export default drawLineString;
